import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { action, computed, observable, reaction } from 'mobx'
import { useTransform } from 'motion/react'
import { useSpringValue } from '@/hooks/motion-hooks'
import { useHotkey } from '@/hooks/keyboard-hooks'

/**
 * Manages entries in the modal stack.
 * Used for maintaining modal stack layer order.
 */
export class ModalStackManager {
  /**
   * Modal entries.
   * @private
   */
  @observable private accessor _stack: ModalStackEntry[] = []

  /**
   * Logical clock used for determining the order in which modals are shown.
   *
   * @private
   */
  @observable private accessor _clock = 0

  /**
   * Entries in the stack that are currently showing.
   * Sorted by their clock values in ascending order.
   * That means the modal that is shown first is returned first.
   */
  @computed
  get showingEntries() {
    const filtered = this._stack.filter((x) => x.showing)
    return filtered.sort((a, b) => a.clock - b.clock)
  }

  /**
   * Returns the layer (position in the stack) for the entry.
   * The top layer is 0.
   */
  getLayerForEntry(entry: ModalStackEntry) {
    const showing = this.showingEntries
    const idx = showing.indexOf(entry)
    if (idx === -1) {
      return 0
    }

    return showing.length - idx - 1
  }

  /**
   * Advances the clock and returns it.
   */
  @action.bound
  tick() {
    this._clock++
    return this._clock
  }

  /**
   * Adds an entry.
   */
  @action.bound
  add() {
    const entry = new ModalStackEntry(this)
    this._stack.push(entry)
    return entry
  }

  /**
   * Removes the entry
   * @param entry
   */
  @action.bound
  removeEntry(entry: ModalStackEntry) {
    const idx = this._stack.indexOf(entry)
    if (idx > -1) {
      this._stack.splice(idx, 1)
    }
  }

  /**
   * Pops the top layer from the stack and closes it.
   */
  @action.bound
  pop() {
    const showing = this.showingEntries
    showing[showing.length - 1]?.pop()
  }
}

/**
 * An entry in the modal stack.
 */
export class ModalStackEntry {
  /**
   * Tracks whether the backing modal is showing.
   */
  @observable accessor showing = false

  /**
   * Whether popping (closing on Esc) is allowed.
   */
  @observable accessor allowPop = true

  /**
   * Determines the order in which modals are shown.
   */
  @observable accessor clock = 0

  /**
   * Backing close method.
   * @private
   */
  private _close?: () => unknown

  /**
   * Constructor.
   * @param _manager
   */
  constructor(private readonly _manager: ModalStackManager) {}

  /**
   * Get the layer position for this entry.
   */
  @computed
  get layer() {
    return this._manager.getLayerForEntry(this)
  }

  /**
   * Called when popping from the stack. Closes if allowed.
   */
  @action.bound
  pop() {
    if (this.allowPop) {
      this._close?.()
    }
  }

  /**
   * Updates the props.
   *
   * @param showing
   * @param close
   * @param allowPop
   */
  @action.bound
  update(
    showing: boolean,
    close: (() => unknown) | undefined,
    allowPop: boolean,
  ) {
    this._close = close
    this.allowPop = allowPop
    if (showing && !this.showing) {
      this.clock = this._manager.tick()
    }
    this.showing = showing
  }
}

/**
 * The stack manager.
 */
const ModalStackContext = createContext<ModalStackManager | null>(null)

/**
 * Props for {@link ModalStackContextProvider}.
 */
interface ModalStackContextProviderProps {
  children?: ReactNode
}

/**
 * Instantiates and passes down a modal stack manager, and closes the top
 * modal on Esc.
 */
export function ModalStackContextProvider({
  children,
}: ModalStackContextProviderProps) {
  const [manager] = useState(() => new ModalStackManager())
  useHotkey(['Escape'], manager.pop)

  return <ModalStackContext value={manager}>{children}</ModalStackContext>
}

/**
 * Gets the modal stack manager.
 */
export function useModalStackManager() {
  const manager = useContext(ModalStackContext)
  if (!manager) {
    throw new Error('Missing modal stack manager context')
  }

  return manager
}

////////////
// Modal stacking hook
////////////

interface UseModalStackingProps {
  isOpen: boolean
  close?: () => unknown
  /**
   * If `true`, disables closing on Esc.
   */
  disableCloseOnEsc?: boolean
}

/**
 * Adds the modal to the modal stack and returns the styles that push it back
 * into z-space while other modals are stacked on top of it.
 */
export function useModalStacking({
  isOpen,
  close,
  disableCloseOnEsc,
}: UseModalStackingProps) {
  const manager = useModalStackManager()
  const [layer, setLayer] = useState(0)

  const entryRef = useRef<ModalStackEntry>(null)

  useEffect(() => {
    if (entryRef.current) {
      return
    }

    const entry = manager.add()
    entryRef.current = entry

    const unsubscribe = reaction(() => entry.layer, setLayer, {
      fireImmediately: true,
    })

    return () => {
      unsubscribe()
      manager.removeEntry(entry)
      entryRef.current = null
    }
  }, [manager])

  useEffect(() => {
    entryRef.current?.update(isOpen, close, !disableCloseOnEsc)
  }, [isOpen, close, disableCloseOnEsc])

  const layerSpring = useSpringValue(layer)
  const stackingTransform = useTransform(
    layerSpring,
    (v) => `translateY(-${v * 15}px) scale(${1 - v * 0.03})`,
  )

  return {
    layer,
    isFirstResponder: isOpen && layer === 0,
    stackingStyle: {
      transformOrigin: 'top center',
      transform: stackingTransform,
    },
  }
}
